/**
 * ═══════════════════════════════════════════════════════════════════════════
 * NETTOYAGE DES DONNÉES ÉTRANGÈRES - SUCCESSLINK AGENCIA
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * Ce fichier contient toutes les fonctions de nettoyage pour supprimer
 * les données des autres sites (Beluga, etc.) et ne garder que les données
 * SUCCESSLINK AGENCIA.
 */

import { getAllUsersFromStorage, deleteUser, siteKey } from './sharedStorage';

// ── Liste des téléphones légitimes SUCCESSLINK ──────────────────────────────
const LEGITIMATE_PHONES = [
  '97800237',  // Admin principal
  '92252664',  // Admin
  '92242664',  // Admin
  '98436291',  // Admin
  '90804284',  // Admin
  '70636321',  // Admin
];

// ── Identifiant unique du site ──────────────────────────────────────────────
export const SITE_ID = 'SUCCESSLINK_AGENCIA';

/**
 * Détecte si un utilisateur est étranger (Beluga, autre site, etc.)
 */
export function isForeignUser(user: any): boolean {
  // Si c'est un admin légitime, garder
  if (LEGITIMATE_PHONES.includes(user.phone)) {
    return false;
  }

  const name = (user.name || '').toLowerCase();
  const username = (user.username || '').toLowerCase();
  const email = (user.email || '').toLowerCase();

  // Détecter les comptes Beluga
  if (name.includes('beluga') || username.includes('beluga') || email.includes('beluga')) {
    return true;
  }

  // Détecter les comptes avec des noms de test
  if (name.includes('test') || username.includes('test')) {
    return true;
  }

  // Détecter les comptes avec des emails suspects
  if (email.includes('beluga') || email.includes('test')) {
    return true;
  }

  return false;
}

/**
 * Supprime tous les utilisateurs étrangers
 */
export async function cleanForeignUsers(): Promise<number> {
  try {
    const allUsers = getAllUsersFromStorage();
    const foreignUsers = allUsers.filter(isForeignUser);

    if (foreignUsers.length === 0) {
      console.log('✅ Aucun utilisateur étranger détecté');
      return 0;
    }

    console.log(`🧹 Suppression de ${foreignUsers.length} utilisateur(s) étranger(s)...`);

    // Supprimer chaque utilisateur étranger
    for (const user of foreignUsers) {
      try {
        await deleteUser(user.phone);
        console.log(`  ✓ Supprimé: ${user.name} (${user.phone})`);
      } catch (error) {
        console.error(`  ✗ Erreur suppression ${user.phone}:`, error);
      }
    }

    return foreignUsers.length;
  } catch (error) {
    console.error('❌ Erreur nettoyage utilisateurs étrangers:', error);
    return 0;
  }
}

/**
 * Supprime toutes les demandes d'activation étrangères
 */
export function cleanForeignActivationRequests(): number {
  try {
    const allUsers = getAllUsersFromStorage();
    const foreignPhones = new Set(
      allUsers.filter(isForeignUser).map(u => u.phone)
    );

    if (foreignPhones.size === 0) {
      return 0;
    }

    const requestKeys = [
      'activationRequests',
      'SUCCESSLINK_V1__activationRequests',
      siteKey('activationRequests'),
    ];

    let totalDeleted = 0;

    for (const key of requestKeys) {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) continue;

        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) continue;

        const before = parsed.length;
        const filtered = parsed.filter((r: any) => !foreignPhones.has(r.userPhone));
        const deleted = before - filtered.length;

        if (deleted > 0) {
          localStorage.setItem(key, JSON.stringify(filtered));
          totalDeleted += deleted;
          console.log(`  ✓ ${key}: ${deleted} demande(s) supprimée(s)`);
        }
      } catch (error) {
        console.error(`  ✗ Erreur nettoyage ${key}:`, error);
      }
    }

    return totalDeleted;
  } catch (error) {
    console.error('❌ Erreur nettoyage demandes activation:', error);
    return 0;
  }
}

/**
 * Supprime toutes les demandes de retrait étrangères
 */
export function cleanForeignWithdrawalRequests(): number {
  try {
    const allUsers = getAllUsersFromStorage();
    const foreignPhones = new Set(
      allUsers.filter(isForeignUser).map(u => u.phone)
    );

    if (foreignPhones.size === 0) {
      return 0;
    }

    const withdrawalKeys = [
      'withdrawalRequests',
      'SUCCESSLINK_V1__withdrawalRequests',
      siteKey('withdrawalRequests'),
    ];

    let totalDeleted = 0;

    for (const key of withdrawalKeys) {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) continue;

        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) continue;

        const before = parsed.length;
        const filtered = parsed.filter((r: any) => !foreignPhones.has(r.userPhone));
        const deleted = before - filtered.length;

        if (deleted > 0) {
          localStorage.setItem(key, JSON.stringify(filtered));
          totalDeleted += deleted;
          console.log(`  ✓ ${key}: ${deleted} retrait(s) supprimé(s)`);
        }
      } catch (error) {
        console.error(`  ✗ Erreur nettoyage ${key}:`, error);
      }
    }

    return totalDeleted;
  } catch (error) {
    console.error('❌ Erreur nettoyage demandes retrait:', error);
    return 0;
  }
}

/**
 * Nettoyage complet de toutes les données étrangères
 */
export async function cleanAllForeignData(): Promise<{
  deletedUsers: number;
  deletedActivationRequests: number;
  deletedWithdrawalRequests: number;
  totalDeleted: number;
}> {
  console.log('🧹 ═══════════════════════════════════════════════════════════');
  console.log('🧹 NETTOYAGE DES DONNÉES ÉTRANGÈRES - SUCCESSLINK AGENCIA');
  console.log('🧹 ═══════════════════════════════════════════════════════════');

  const deletedUsers = await cleanForeignUsers();
  const deletedActivationRequests = cleanForeignActivationRequests();
  const deletedWithdrawalRequests = cleanForeignWithdrawalRequests();

  const result = {
    deletedUsers,
    deletedActivationRequests,
    deletedWithdrawalRequests,
    totalDeleted: deletedUsers + deletedActivationRequests + deletedWithdrawalRequests,
  };

  console.log('');
  console.log('✅ ═══════════════════════════════════════════════════════════');
  console.log('✅ NETTOYAGE TERMINÉ');
  console.log('✅ ═══════════════════════════════════════════════════════════');
  console.log(`   • Utilisateurs supprimés: ${deletedUsers}`);
  console.log(`   • Demandes d'activation supprimées: ${deletedActivationRequests}`);
  console.log(`   • Demandes de retrait supprimées: ${deletedWithdrawalRequests}`);
  console.log(`   • TOTAL: ${result.totalDeleted} élément(s) supprimé(s)`);
  console.log('✅ ═══════════════════════════════════════════════════════════');

  return result;
}

/**
 * Vérifie si le nettoyage automatique a déjà été effectué aujourd'hui
 */
export function shouldRunAutoCleanup(): boolean {
  const lastCleanup = localStorage.getItem('lastAutoCleanup');
  if (!lastCleanup) return true;

  const lastDate = new Date(lastCleanup);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return lastDate < today;
}

/**
 * Marque le nettoyage automatique comme effectué
 */
export function markAutoCleanupDone(): void {
  localStorage.setItem('lastAutoCleanup', new Date().toISOString());
}